// The site header — one bar, every public page.
//
// It lives here rather than in Home because the borrower page needs the SAME
// header, not a lookalike. The borrower page used to carry its own: a different
// logo treatment, a different link set, a mega menu and its own CTA. Moving
// between the two read like leaving the site and arriving somewhere related.
//
// One thing here is less obvious than it looks: a nav entry is either a PAGE
// (`to`) or a SECTION of the marketing home (`id`), and a section entry has to
// work from a page that doesn't contain that section. See goSection below.

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BrandLogo from './BrandLogo';
import NavAuthCtas, { AuthMenuRows, NavLoginMenu } from './NavAuthCtas';
// 2026-09-26: the Distributor header Login goes to the STAFF app's login, the
// same place every Distributor plan button goes.
import { DISTRIBUTOR_LOGIN_URL } from './staffAppUrl';

/**
 * Where a distributor signs in: the staff (B2B) app. (Lenders do not sign in
 * from this site — they register through the Lender page's form.) The exact URL
 * the Distributor plan cards use (distributor-flow.js GET_STARTED_URL), so the
 * header and the plans can never send a partner to two different places.
 * Always https://app.interestng.com/app/login, local dev included (see
 * DISTRIBUTOR_LOGIN_URL).
 */
export const PARTNER_LOGIN_URL = DISTRIBUTOR_LOGIN_URL;

// Home leads, then the three audiences. Home is also the only way back: the
// audience pages carry no navigation of their own, so without it the header
// pointed outward from home and never home again.
//
// A "Pricing" entry used to close the list. Removed on request — and it had
// never actually worked: it was a SECTION link ({id}, not {to}), but no element
// with id="pricing" exists anywhere in the app, so every click fell through to
// the route-home-and-carry-the-intent branch in goSection, and nothing reads
// that intent either. It was a control that looked live and did nothing.
//
// That leaves no {id} entries at all today. The section-link branch below is
// kept rather than deleted because it is the mechanism any future in-page link
// would use, and it costs one ternary.
// (2026-09-04: id="pricing" now DOES exist — the distributor page's
// <PricingSection> carries it, and the header's "Get started" scrolls there.
// The claim above was true when written.)
export const NAV_SECTIONS = [
  { to: '/', label: 'Home' },
  { to: '/borrower', label: 'Borrower' },
  // "Loan Distributors", not "Distributor": asked for on 2026-09-05 to match the
  // home page's card, which now reads FOR LOAN DISTRIBUTORS. Deliberately out of
  // step with its singular neighbours — the wording was the point.
  { to: '/distributor', label: 'Loan Distributors' },
  { to: '/lender', label: 'Lender' },
  // 'Pricing' (/pricing) sat here for part of 2026-09-05 and was taken back out
  // on request. The PAGE is still built and still routed in App.js — only the
  // nav entry is gone, so the page is reachable by its URL and by any link that
  // points at it. Put this line back to return it to the bar.
];

/** The state key Home reads to finish a cross-page section jump. */
export const SCROLL_INTENT = 'scrollToSection';

/**
 * 2026-09-26 (UX review, P0: "existing users cannot easily find the Login
 * option"): every public page carries Login and Get Started in the bar — the
 * lists below only decide WHICH login and which Get Started:
 *   /borrower       Login → /login (this app)          · Get Started → the wizard
 *   /distributor    Login → PARTNER_LOGIN_URL           · Get Started → #pricing
 *   /lender         Login ▾ (Borrower | Distributor)    · Get Started → register form
 *   anything else   Login ▾ (Borrower | Distributor)    · Get Started → /borrower
 * The Lender page had no Login at all until UAT (#18) found it the one public
 * bar without one. Lenders still have no account of their own — they
 * register — so it is the neutral menu every other page carries: a borrower
 * or a distributor who lands on /lender can still sign in from the bar.
 *   (the home page, pricing, the legal pages)
 * The notes below describe the per-route choices as they were made.
 *
 * Pages that get Login + "Talk to Interestng AI" in the bar.
 *
 * The header is ONE instance mounted above the routes (App.js), so a page cannot
 * hand it anything directly — the header is the ancestor, not the child. Which
 * routes want the controls is therefore stated here, in the header itself.
 *
 * Kept as a list rather than a `=== '/borrower'` so adding a second page later
 * is a one-word edit and the intent stays readable.
 */
const ROUTES_WITH_AUTH_CTAS = ['/borrower'];

/**
 * Routes that get a "Get started" in the bar, and where it sends them.
 *
 * 2026-09-04: it used to leave for the STAFF app login
 * (staffAppUrl('/app/login') — a real <a href> that leaves the SPA, resolved
 * from the browser's hostname so it follows the open tenant subdomain). On
 * request it now scrolls to the distributor page's on-page pricing section
 * instead (id="pricing" — see <PricingSection id="pricing"> in
 * distributor-flow.js), through the same goSection mechanism kept below for
 * exactly this kind of in-page link.
 */
const ROUTES_WITH_GET_STARTED = ['/distributor'];

/**
 * Routes whose "Get started" opens the LENDER REGISTRATION form instead of
 * leaving for the staff app (2026-09-05). A lender is not a user yet — there
 * is nothing to log in to — so the button asks the page to open its form,
 * through a window event for the same reason the chat launcher does (below).
 */
const ROUTES_WITH_LENDER_REGISTER = ['/lender'];

/**
 * How the header asks the current page to open its journey picker. (The event
 * name predates the wizard; it is kept so nothing listening has to change.)
 *
 * A window event rather than a prop or a context, because the chat belongs to
 * the PAGE — borrower-flow owns the wizard, the guest session and the plan
 * picker — and the header sits above it in the tree with no way to reach in.
 * The alternative, hoisting the whole conversation into the layout, would move
 * a great deal of page state up for one button.
 *
 * The codebase already signals across this seam the same way (see
 * UNAUTHORIZED_EVENT in features/api/consumerClient), so this is the existing
 * convention rather than a new one.
 */
export const OPEN_CHAT_EVENT = 'interestng:open-chat';

/** How the header asks the Lender page to open its registration form. */
export const OPEN_LENDER_REGISTER_EVENT = 'interestng:open-lender-register';

export default function SiteHeader() {
  const nav = useNavigate();
  const loc = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const navMenuRef = useRef(null);

  // Only PAGE entries ({to}) can be current. A section entry ({id}) is part of
  // a page rather than a page of its own, so marking it active would tell the
  // reader they are somewhere they are not. Every entry is a page today.
  // The path as React Router matches it: no trailing slash, any case. Without
  // this, /distributor/ and /Distributor rendered the Distributor page (plan
  // buttons and all) under the neutral "Login ▾" bar (2026-09-26).
  const path = (loc.pathname.replace(/\/+$/, '') || '/').toLowerCase();
  const isCurrent = (s) => !!s.to && path === s.to;
  const showAuthCtas = ROUTES_WITH_AUTH_CTAS.includes(path);
  const showGetStarted = ROUTES_WITH_GET_STARTED.includes(path);
  const showLenderRegister = ROUTES_WITH_LENDER_REGISTER.includes(path);
  // Every other public page (the home page, pricing, the legal pages): a Login
  // menu that asks which kind of account, and Get Started to the Borrower page.
  const showNeutral = !showAuthCtas && !showGetStarted && !showLenderRegister;

  // Close on outside click and on Escape — a disclosure the keyboard can open
  // must be one the keyboard can close.
  useEffect(() => {
    const onDoc = (e) => {
      if (navMenuRef.current && !navMenuRef.current.contains(e.target)) setNavOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setNavOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // A section link scrolls when the section is on THIS page, and routes home
  // carrying the intent when it isn't. Without the second branch a section link
  // would be a dead control on every page but the one holding it — present,
  // clickable, and doing nothing, which is worse than not being there.
  // NAV_SECTIONS still carries no {id} entries, but since 2026-09-04 the
  // distributor "Get started" CTA below reaches this to scroll to the page's
  // pricing section (id="pricing").
  const goSection = (id) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: 'smooth' }); return; }
    nav('/', { state: { [SCROLL_INTENT]: id } });
  };

  const go = (s) => (s.to ? nav(s.to) : goSection(s.id));

  return (
    <nav className="nav">
      <div className="nav-in">
        <BrandLogo height={34} />
        {/* Desktop: inline links. One source of truth (NAV_SECTIONS) drives both
            this and the mobile menu below, so they can never drift apart. */}
        <div className="nav-links">
          {NAV_SECTIONS.map((s) => (
            <button
              key={s.to || s.id}
              type="button"
              className={isCurrent(s) ? 'is-active' : undefined}
              aria-current={isCurrent(s) ? 'page' : undefined}
              onClick={() => go(s)}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Mobile: the same destinations behind a disclosure. They used to be
            `display:none` below 820px with NO replacement, so the sections were
            unreachable by navigation on a phone — the primary device. */}
        <div className="nav-more" ref={navMenuRef}>
          <button
            type="button"
            className="nav-more-btn"
            onClick={() => setNavOpen((v) => !v)}
            aria-expanded={navOpen}
            aria-controls="nav-more-menu"
            aria-label={navOpen ? 'Close the menu' : 'Open the menu'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              {navOpen ? (
                <><line x1="5" y1="5" x2="19" y2="19" /><line x1="19" y1="5" x2="5" y2="19" /></>
              ) : (
                <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>
              )}
            </svg>
          </button>
          {navOpen && (
            <div className="nav-more-menu" id="nav-more-menu">
              {NAV_SECTIONS.map((s) => (
                <button
                  key={s.to || s.id}
                  type="button"
                  className={isCurrent(s) ? 'is-active' : undefined}
                  aria-current={isCurrent(s) ? 'page' : undefined}
                  onClick={() => { setNavOpen(false); go(s); }}
                >
                  {s.label}
                </button>
              ))}
              {/* Login lands here too — the only place it shows below 360px,
                  where the bar has no room for it. Below the destinations and
                  behind a rule, because it is a
                  different KIND of row — the ones above move you around the
                  site, this one changes who you are on it. */}
              {/* 2026-09-26: one row per kind of account (see AuthMenuRows) —
                  on the Lender page too since UAT #18 (lenders themselves
                  have no login; a borrower or distributor there does). */}
              <AuthMenuRows onNavigate={() => setNavOpen(false)} partnerLoginUrl={PARTNER_LOGIN_URL} />
            </div>
          )}
        </div>

        {/* Login + the chat launcher, on the routes that ask for them. `.nav-in`
            is a three-track grid (logo | links | actions) and this is the last
            track, so it ends at the bar's right edge by layout. It used to rely
            on the links being absolutely positioned and this carrying
            margin-left:auto — which let the cluster grow leftwards OVER the
            links once signing in added a third control. */}
        {showAuthCtas && (
          <NavAuthCtas onTalk={() => window.dispatchEvent(new CustomEvent(OPEN_CHAT_EVENT))} />
        )}

        {/* The distributor's way in. Same `.nav-cta` slot, so it lands in the
            same place as the borrower page's cluster.
            2026-09-04: was href={staffAppUrl('/app/login')} — now scrolls to
            the page's pricing section, on request (see the
            ROUTES_WITH_GET_STARTED note above). */}
        {/* 2026-09-26: Login beside it — the staff app's login, the same URL
            every plan card below uses. "Get Started" capitalised to match
            every other page (CTA consistency, UX review P1). */}
        {showGetStarted && (
          <div className="nav-cta">
            {/* Same look as the Borrower page's Login (btn btn-g btn-sm
                nav-login), on request; same link as the plan buttons below. */}
            <a className="btn btn-g btn-sm nav-login" href={PARTNER_LOGIN_URL}>Login</a>
            <a
              className="btn btn-p btn-ing btn-sm"
              href="#pricing"
              onClick={(e) => { e.preventDefault(); goSection('pricing'); }}
            >
              Get Started
            </a>
          </div>
        )}

        {/* The lender's way in: the registration form, not a login. Lenders
            have no login — they leave their details and the team follows up.
            The Login menu beside it is the neutral one (Borrower |
            Distributor), so the bar has the Login every public page has (#18). */}
        {showLenderRegister && (
          <div className="nav-cta">
            <NavLoginMenu partnerLoginUrl={PARTNER_LOGIN_URL} />
            <button
              type="button"
              className="btn btn-p btn-ing btn-sm"
              onClick={() => window.dispatchEvent(new CustomEvent(OPEN_LENDER_REGISTER_EVENT))}
            >
              Get Started
            </button>
          </div>
        )}

        {/* Every other public page (2026-09-26): one Login that asks which
            account — a borrower's (this app) or a distributor's (the staff
            app) — and Get Started to the Borrower page. */}
        {showNeutral && (
          <div className="nav-cta">
            <NavLoginMenu partnerLoginUrl={PARTNER_LOGIN_URL} />
            <button type="button" className="btn btn-p btn-ing btn-sm" onClick={() => nav('/borrower')}>
              Get Started
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}
